// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { isAfter, isBefore, isSameDay, parseISO, startOfDay } from 'date-fns';
import {
  ConditionalFormattingOperator,
  ConditionalFormattingRule,
} from '../../../models/widget/conditional-formatting.model';
import { normalizeString } from '../../../util';

export function getMatchRuleForValue(
  value: string,
  rules: ConditionalFormattingRule[],
): ConditionalFormattingRule | null {
  const matchingRule = rules.find((rule) => {
    const operatorFunction = operatorFunctions[rule.operator];
    return operatorFunction(rule.value as string, value);
  });
  return matchingRule || null;
}

export const operatorFunctions: Record<
  ConditionalFormattingOperator,
  (ruleValue: string, dataValue: string) => boolean
> = {
  // All
  equal: (ruleValue: unknown, dataValue: unknown) => `${ruleValue}` === `${dataValue}`,
  not_equal: (ruleValue: unknown, dataValue: unknown) => `${ruleValue}` !== `${dataValue}`,

  // String
  contains: (ruleValue: string, dataValue: string) =>
    normalizeString(`${dataValue}`).includes(normalizeString(ruleValue)),
  not_contains: (ruleValue: string, dataValue: string) =>
    !normalizeString(`${dataValue}`).includes(normalizeString(ruleValue)),
  starts_with: (ruleValue: string, dataValue: string) =>
    normalizeString(`${dataValue}`).startsWith(normalizeString(ruleValue)),
  ends_with: (ruleValue: string, dataValue: string) =>
    normalizeString(`${dataValue}`).endsWith(normalizeString(ruleValue)),

  // Number
  more_than: (ruleValue: string, dataValue: string) => {
    try {
      const [parsedRuleValue, parsedDataValue] = getNumbers(ruleValue, dataValue);
      return parsedDataValue > parsedRuleValue;
    } catch {
      return false;
    }
  },
  more_than_or_equal: (ruleValue: string, dataValue: string) => {
    try {
      const [parsedRuleValue, parsedDataValue] = getNumbers(ruleValue, dataValue);
      return parsedDataValue >= parsedRuleValue;
    } catch {
      return false;
    }
  },
  less_than: (ruleValue: string, dataValue: string) => {
    try {
      const [parsedRuleValue, parsedDataValue] = getNumbers(ruleValue, dataValue);
      return parsedDataValue < parsedRuleValue;
    } catch {
      return false;
    }
  },
  less_than_or_equal: (ruleValue: string, dataValue: string) => {
    try {
      const [parsedRuleValue, parsedDataValue] = getNumbers(ruleValue, dataValue);
      return parsedDataValue <= parsedRuleValue;
    } catch {
      return false;
    }
  },

  // Date
  day_is: (ruleValue: string, dataValue: string) => {
    try {
      return isSameDay(...getDates(ruleValue, dataValue));
    } catch {
      return false;
    }
  },
  day_is_before: (ruleValue: string, dataValue: string) => {
    try {
      const [parsedRuleValue, parsedDataValue] = getDates(ruleValue, dataValue);
      return isBefore(startOfDay(parsedDataValue), startOfDay(parsedRuleValue));
    } catch {
      return false;
    }
  },
  day_is_after: (ruleValue: string, dataValue: string) => {
    try {
      const [parsedRuleValue, parsedDataValue] = getDates(ruleValue, dataValue);
      return isAfter(startOfDay(parsedDataValue), startOfDay(parsedRuleValue));
    } catch {
      return false;
    }
  },
};

function getNumbers(a: string, b: string): [number, number] {
  const parsedA = parseFloat(`${a}`);
  const parsedB = parseFloat(`${b}`);
  if (isNaN(parsedA) || isNaN(parsedB)) {
    throw new Error();
  }
  return [parsedA, parsedB];
}

function getDates(a: string, b: string): [Date, Date] {
  const dateA = parseISO(a);
  const dateB = parseISO(b);
  return [dateA, dateB];
}
